import { createBrowserHistory } from 'history';
import { toInternal, toPublicTo } from './deployment';

/* ══════════════════════════════════════════════════════════════════════════
 * Translating history for public-domain deployments.
 *
 * Wraps a real browser history so that:
 *   - the ROUTER reads internal paths  (address bar /merch/x → /clothing/abc-se/x)
 *   - the ADDRESS BAR shows public paths (push('/clothing/abc-se/x') → /merch/x)
 *
 * React Router resolves relative navigations against the current (internal)
 * location BEFORE calling the navigator, so push/replace always receive
 * absolute internal paths. search/hash/state/key pass through untouched.
 * Used with react-router-dom's unstable_HistoryRouter (see index.js).
 * ══════════════════════════════════════════════════════════════════════════ */

export default function createTranslatingHistory(dep) {
  const inner = createBrowserHistory();

  const internalLocation = (location) => ({
    ...location,
    pathname: toInternal(location.pathname, dep),
  });

  return {
    get action() {
      return inner.action;
    },
    get location() {
      return internalLocation(inner.location);
    },
    createHref: (to) => inner.createHref(toPublicTo(to, dep)),
    push: (to, state) => inner.push(toPublicTo(to, dep), state),
    replace: (to, state) => inner.replace(toPublicTo(to, dep), state),
    go: (delta) => inner.go(delta),
    back: () => inner.go(-1),
    forward: () => inner.go(1),
    listen: (listener) =>
      inner.listen(({ action, location }) =>
        listener({ action, location: internalLocation(location) })
      ),
    block: (blocker) =>
      inner.block((tx) => blocker({ ...tx, location: internalLocation(tx.location) })),
  };
}
